import { Card, CardContent } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { useOpportunityStats, type OpportunityFilters } from '@/hooks/api/useOpportunities';
import { formatCurrencyCompact, formatNumber } from '@/lib/formatters';

interface DashboardStatsProps {
  filters?: Partial<OpportunityFilters>;
}

export function DashboardStats({ filters }: DashboardStatsProps) {
  const { data: stats, isLoading, error } = useOpportunityStats(filters);

  if (isLoading) {
    return (
      <Card
        className="bg-gradient-card"
        style={{ boxShadow: 'var(--shadow-1), var(--inset-highlight)' }}
      >
        <CardContent className="px-4 py-2.5 space-y-1">
          {Array.from({ length: 4 }).map((_, i) => (
            <div key={i} className="flex items-center justify-between">
              <Skeleton className="h-3 w-24" />
              <Skeleton className="h-3 w-12" />
            </div>
          ))}
        </CardContent>
      </Card>
    );
  }

  if (error || !stats) return null;

  const items = [
    { label: 'Total Opportunities', value: formatNumber(stats.totalOpportunities) },
    { label: 'Total Est. Value', value: formatCurrencyCompact(stats.totalEstimatedValue) },
    { label: 'High Score (80+)', value: formatNumber(stats.scoreDistribution.high) },
    { label: 'New Opportunities', value: formatNumber(stats.byStatus.new) },
  ];

  return (
    <Card
      className="bg-gradient-card"
      style={{ boxShadow: 'var(--shadow-1), var(--inset-highlight)' }}
    >
      <CardContent className="px-4 py-2.5 space-y-1">
        {items.map((item) => (
          <div key={item.label} className="flex items-center justify-between">
            <span className="text-xs text-muted-foreground">{item.label}</span>
            <span className="text-sm font-semibold font-tabular tracking-tight">{item.value}</span>
          </div>
        ))}
      </CardContent>
    </Card>
  );
}
